import React, { useEffect } from 'react';

import { FormikValues, useFormik } from 'formik';

import {
  getPharmacyAdminLists,
  IPharmacyAdminListApiParams,
  pharmacyAdminList,
} from '@pharmaplan/common';

import usePharmacyAdministratorActions from '../../../hooks/Admin/PharmacyAdministrators/usePharmacyAdministratorActions';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';
import useUserProfileLink from '../../../hooks/Admin/useUserProfileLink';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useTableSort from '../../../hooks/useTableSort';
import { IDynamicTableObject } from '../../DynamicTable/types';
import DynamicTable from '../../DynamicTable';
import strings from '../../../localization';

import PharmacyAdminHeader from './PharmacyAdminHeader';
import { pharmacyAdminHeaders, pharmacyAdminRows } from './helper';

const initialValues = {
  adminText: '',
  pharmacyText: '',
};

const PharmacyAdministrators = () => {
  const dispatch = useAppDispatch();

  const list = useAppSelector(pharmacyAdminList);
  const { can } = useAdminPermissions();
  const { goToUserProfile } = useUserProfileLink();
  const { isDesc, orderBy, handleSorting, order } = useTableSort();

  const apiCall = (params?: IPharmacyAdminListApiParams) => {
    dispatch(getPharmacyAdminLists(params ?? initialValues));
  };

  const handleSubmit = (values: FormikValues) => {
    apiCall(values as IPharmacyAdminListApiParams);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
  });

  const { editAction, deleteAction, loadSuccess } = usePharmacyAdministratorActions(formik.values);

  const table: IDynamicTableObject = {
    title: strings.pharmacyAdministrators,
    headers: pharmacyAdminHeaders(can),
    rows: pharmacyAdminRows({
      data: list,
      deleteAction,
      editAction,
      goToUserProfile,
      can,
    }),
    headerBar: [],
  };

  useEffect(() => {
    apiCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    formik.resetForm();
    apiCall();
  };

  useEffect(() => {
    if (orderBy) {
      apiCall({ paging: { sortBy: orderBy, desc: isDesc }, ...formik.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return (
    <DynamicTable
      table={table}
      loadSuccess={loadSuccess}
      showHeader={false}
      order={order}
      orderBy={orderBy}
      handleSort={handleSorting}
      hidePagination
      customHeader={
        <PharmacyAdminHeader formik={formik} handleReset={handleReset} />
      }
    />
  );
};

export default PharmacyAdministrators;
